import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export interface LocationContextType {
  currentStore: string | null // null means "not in supermarket"
  setCurrentStore: (store: string | null) => void
  isLocationModalOpen: boolean
  setLocationModalOpen: (open: boolean) => void
}

const LocationContext = createContext<LocationContextType | undefined>(undefined)

export const useLocation = () => {
  const context = useContext(LocationContext)
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider')
  }
  return context
}

interface LocationProviderProps {
  children: ReactNode
}

export const LocationProvider = ({ children }: LocationProviderProps) => {
  const [currentStore, setCurrentStoreState] = useState<string | null>(null)
  const [isLocationModalOpen, setLocationModalOpen] = useState(false)

  // Load saved location from localStorage on mount
  useEffect(() => {
    const savedStore = localStorage.getItem('currentStore')
    if (savedStore && savedStore !== 'null') {
      setCurrentStoreState(savedStore)
    }
  }, [])

  // Save to localStorage whenever store changes
  const setCurrentStore = (store: string | null) => {
    setCurrentStoreState(store)
    if (store === null) {
      localStorage.removeItem('currentStore')
    } else {
      localStorage.setItem('currentStore', store)
    }
  }

  return (
    <LocationContext.Provider
      value={{
        currentStore,
        setCurrentStore,
        isLocationModalOpen,
        setLocationModalOpen,
      }}
    >
      {children}
    </LocationContext.Provider>
  )
}